// Hook for PWA Install Prompt
// Manages the beforeinstallprompt event and install state

import { useState, useEffect, useCallback } from 'react';

// BeforeInstallPromptEvent is not in standard TypeScript types
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface InstallPromptState {
  canInstall: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  installOutcome: 'accepted' | 'dismissed' | null;
}

interface UseInstallPromptReturn extends InstallPromptState {
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  dismissPrompt: () => void;
  shouldShowPrompt: () => boolean;
  incrementVisitCount: () => number;
  getVisitCount: () => number;
}

const STORAGE_KEY = 'trackfleet-pwa-install';
const DISMISS_KEY = 'trackfleet-pwa-dismissed';
const VISIT_COUNT_KEY = 'trackfleet-visit-count';
const MIN_VISITS_FOR_PROMPT = 1;

// Store the deferred prompt event
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function useInstallPrompt(): UseInstallPromptReturn {
  const [state, setState] = useState<InstallPromptState>(() => {
    // Detect platform and install state
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error navigator.standalone is iOS-specific
      window.navigator.standalone === true;

    return {
      canInstall: false,
      isInstalled: isStandalone,
      isIOS,
      isAndroid,
      isStandalone,
      installOutcome: null,
    };
  });

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event for later use
      deferredPrompt = e as BeforeInstallPromptEvent;
      setState((prev) => ({ ...prev, canInstall: true }));
      console.log('[PWA] Install prompt available');
    };

    const handleAppInstalled = () => {
      deferredPrompt = null;
      setState((prev) => ({
        ...prev,
        canInstall: false,
        isInstalled: true,
        isStandalone: true,
      }));
      // Store installed state
      localStorage.setItem(STORAGE_KEY, 'installed');
      console.log('[PWA] App installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if already installed via localStorage
    const storedState = localStorage.getItem(STORAGE_KEY);
    if (storedState === 'installed') {
      setState((prev) => ({ ...prev, isInstalled: true }));
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Listen for display mode changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const handleChange = (e: MediaQueryListEvent) => {
      setState((prev) => ({
        ...prev,
        isStandalone: e.matches,
        isInstalled: e.matches,
      }));
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Trigger the install prompt
  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferredPrompt) {
      console.log('[PWA] Install prompt not available');
      return 'unavailable';
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] User choice:', outcome);

      setState((prev) => ({ ...prev, installOutcome: outcome }));

      if (outcome === 'accepted') {
        deferredPrompt = null;
        setState((prev) => ({ ...prev, canInstall: false }));
      }

      return outcome;
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      return 'unavailable';
    }
  }, []);

  // Dismiss the prompt (store in localStorage)
  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setState((prev) => ({ ...prev, installOutcome: 'dismissed' }));
  }, []);

  // Get visit count
  const getVisitCount = useCallback((): number => {
    const count = localStorage.getItem(VISIT_COUNT_KEY);
    return count ? parseInt(count, 10) : 0;
  }, []);

  // Check if we should show the prompt
  const shouldShowPrompt = useCallback((): boolean => {
    // Don't show if already installed
    if (state.isInstalled || state.isStandalone) {
      return false;
    }

    // Don't show if can't install (no prompt event)
    if (!state.canInstall && !state.isIOS) {
      return false;
    }

    // Check if dismissed recently (within 7 days)
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDays) {
        return false;
      }
    }

    // Check visit count
    const visitCount = getVisitCount();
    if (visitCount < MIN_VISITS_FOR_PROMPT) {
      return false;
    }

    return true;
  }, [state.isInstalled, state.isStandalone, state.canInstall, state.isIOS, getVisitCount]);

  // Increment visit count
  const incrementVisitCount = useCallback((): number => {
    const currentCount = getVisitCount();
    const newCount = currentCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    return newCount;
  }, [getVisitCount]);

  return {
    ...state,
    promptInstall,
    dismissPrompt,
    shouldShowPrompt,
    incrementVisitCount,
    getVisitCount,
  };
}

// Simple hook to check if app is installed
export function useIsInstalled(): boolean {
  const [isInstalled, setIsInstalled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error navigator.standalone is iOS-specific
      window.navigator.standalone === true ||
      localStorage.getItem(STORAGE_KEY) === 'installed'
    );
  });

  useEffect(() => {
    const handleAppInstalled = () => setIsInstalled(true);
    window.addEventListener('appinstalled', handleAppInstalled);

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e: MediaQueryListEvent) => setIsInstalled(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isInstalled;
}

export default useInstallPrompt;
